import type { GetStaticProps } from "next";
import { Fragment } from "react";
import type {
  AnnouncementQuery,
  FaqCoreDataFragment,
  PlanCoreDataFragment,
  PostsQuery,
  ServiceCoreDataFragment,
  TestimonialCoreDataFragment,
} from "~/__generated__/graphql";
import { getSdk } from "~/__generated__/graphql";
import Landing from "~/components/landing";
import Metadata from "~/components/metadata";
import StructuredData from "~/components/structured-data";
import client from "~/config/client";

type Props = {
  data: {
    faqs: FaqCoreDataFragment[];
    plans: PlanCoreDataFragment[];
    posts: NonNullable<PostsQuery["posts"]>["data"];
    services: ServiceCoreDataFragment[];
    testimonials: TestimonialCoreDataFragment[];
    announcement: AnnouncementQuery["announcement"];
  };
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  const sdk = getSdk(client);

  const { landing } = await sdk.LandingContents();
  const { faqs, services, testimonials } = Object.assign(
    {
      faqs: [],
      services: [],
      testimonials: [],
    },
    landing?.data?.attributes,
  );

  const { pricing } = await sdk.PricingContents();
  const { announcement } = await sdk.Announcement();

  const { plans } = Object.assign({ plans: [] }, pricing?.data?.attributes);

  const posts =
    (
      await sdk.Posts({
        sort: ["views:desc", "publishedAt:desc"],
        pagination: {
          limit: 3,
        },
      })
    ).posts?.data ?? [];

  return {
    revalidate: 1,
    props: {
      data: {
        faqs,
        plans,
        posts,
        services,
        testimonials,
        announcement,
      },
    },
  };
};

export default function LandingPage(props: Props) {
  return (
    <Fragment>
      <Metadata />
      <StructuredData
        data={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Advante",
          alternateName: "Advante Coaching",
          url: "https://advante.co",
          logo: "https://advante.co/images/advante-logo.webp",
          sameAs: [
            "https://www.facebook.com/advanteco",
            "https://twitter.com/advanteco",
            "https://www.linkedin.com/company/advanteco",
          ],
        }}
      />

      <Landing {...props} />
    </Fragment>
  );
}
